import pLimit from 'p-limit';
import { backOff } from 'exponential-backoff';
import spotifyApi from './spotifyApi';
// import AnalyzeSong from './AnalyzeSong.js';
import ReactGA from 'react-ga4';
import AnalyzeSongsBatch from './AnalyzeSongsBatch.js'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { deleteDoc } from 'firebase/firestore';




// rate-limited API wrapper
const rateLimitedApi = {
  async call(fn, ...args) {
    return backOff(async () => {
      try {
        return await fn(...args);
      } catch (error) {
        if (error.status === 429) {
          const retryAfter = parseInt(error.headers?.['retry-after'] || '1', 10) * 1000;
          console.log(`Rate limited, waiting for ${retryAfter}ms`);
          await new Promise(resolve => setTimeout(resolve, retryAfter));
        }
        throw error;
      }
    }, {
      numOfAttempts: 5,
      startingDelay: 300,
      maxDelay: 10000,
      jitter: true
    });
  }
};


const CleanPlaylist = async (playlistId, chosenFilters, onProgressUpdate, signal) => {

  console.log("CLEAN PLAYLIST COMP: The user and playlist id's are:", playlistId);
  console.log("CLEAN P COMP: Chosen filters - ", chosenFilters);
  const sessionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  let playlistName = '';
  let totalTrackCount = 0;
  
  // get cache 
  const getCleanTrackCache = () => {
    try {
      const cache = JSON.parse(localStorage.getItem('cleanTrackCache')) || {};
      const now = Date.now();
      const filtered = Object.entries(cache).reduce((acc, [key, value]) => {
        if (!value.timestamp || value.timestamp > now - (7 * 24 * 60 * 60 * 1000)) {
          acc[key] = value;
        }
        return acc;
      }, {});
      return filtered;
    } catch (error) {
      console.error("Error reading cache:", error);
      return {};
    }
  };

  const cleanTrackCache = getCleanTrackCache();

  const setCachedTrack = (key, track) => {
    cleanTrackCache[key] = { 
      data: track, 
      timestamp: Date.now() 
    };
    try {
      localStorage.setItem('cleanTrackCache', JSON.stringify(cleanTrackCache));
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  };

  const isValidTrack = (trackItem) => {
    const track = trackItem.track;
    if (!track) return false;
    if (!track.id || !track.name || !track.uri) return false;

    const album = track.album;
    if (!album || !album.id || !album.name || album.images.length === 0) return false;

    if (!track.artists || track.artists.length === 0 || !track.artists[0].id || !track.artists[0].name)
      return false;

    return true;
  };

  const getPlaylistTracks = async (id) => {
    try {
      const response = await rateLimitedApi.call(spotifyApi.getPlaylist.bind(spotifyApi), id);
      playlistName = response.name;
      
      let allTracks = [];
      let { items, next } = response.tracks;
      allTracks = allTracks.concat(items.filter(isValidTrack));
      
      totalTrackCount = response.tracks.total;
   
      while (next) {
        const pagedResponse = await rateLimitedApi.call(
          spotifyApi.getPlaylistTracks.bind(spotifyApi), 
          id, 
          { offset: allTracks.length, limit: 100 }
        );
        
        const validTracks = pagedResponse.items.filter(isValidTrack);
        allTracks = allTracks.concat(validTracks);
        next = pagedResponse.next;
     }
      
      return allTracks;
    } catch (error) {
      console.error("Error fetching playlist data:", error);
      throw error;
    }
  };


  const callAnalyzeBatchWithRetry = async (
    songs,
    chosenFilters,
    batchContext,
    signal,
    maxRetries = 5
  ) => {
    let attempt = 0;
  
    while (attempt < maxRetries) {
      try {
        return await AnalyzeSongsBatch(
          songs,
          chosenFilters,
          batchContext,
          signal
        );
      } catch (err) {
        if (signal?.aborted) throw err;

        console.log("Error obj in analyze songs batch", err)
  
        const status = err?.response?.status;
  
        if (status === 429) {
          console.log("Status is a 429... retry")
          attempt++;
  
          const retryAfterMs =
            err.response?.data?.retryAfterMs ?? (1500 * attempt);
  
          console.warn(
            `Batch ${batchContext.batchNumber} rate limited. ` +
            `Retrying in ${retryAfterMs}ms (attempt ${attempt}/${maxRetries})`
          );
  
          await new Promise(r => setTimeout(r, retryAfterMs));
          continue;
        }
          throw err;
      }
    }
  
    throw new Error(
      `Batch ${batchContext.batchNumber} failed after ${maxRetries} retries due to rate limits`
    );
  };
  
  const analyzeTracksData = async (tracks) => {

    const CHUNK_SIZE = 7; 
    const CHUNK_CONCURRENCY = 2; 
    const limit = pLimit(CHUNK_CONCURRENCY);
  
    const results = {
      explicitTracks: [],
      cleanTracks: [],
      tracksNeedingCleanSearch: []
    };

    let processedSongs = 0;
    const totalBatches = Math.ceil(tracks.length / CHUNK_SIZE);

 
    console.log(`Processing ${tracks.length} tracks in chunks of ${CHUNK_SIZE}`);
          
    const chunkPromises = [];
  
    for (let i = 0; i < tracks.length; i += CHUNK_SIZE) {
      const chunk = tracks.slice(i, i + CHUNK_SIZE);
      const chunkNumber = Math.floor(i / CHUNK_SIZE) + 1;
  
      chunkPromises.push(limit(async () => {
    
        const chunkStart = Date.now();
  
        const songs = chunk.map(trackItem => {
          let localItemName = trackItem.track.name.replace(/\s*-\s*(radio edit|version|mix|edit|remaster|acoustic|live|instrumental|single).*$/i, '').trim();

          let duration = trackItem.track.duration_ms / 1000

          let artists = trackItem.track.artists.map(a => a.name)

          return {
            songTitle: localItemName,
            songArtists: artists,
            songAlbum:  trackItem.track.album.name,
            songDuration: duration
          };
        });
  
        console.log(`ANALYZING CHUNK ${chunkNumber}/${totalBatches} (${songs.length} songs)`);
  
        try {

          const chunkResults = await callAnalyzeBatchWithRetry(
            songs,
            chosenFilters,
            {
              batchNumber: chunkNumber,
              totalBatches: totalBatches,
              totalSongs: tracks.length,
              sessionId: sessionId
            },
            signal
          );
          
         
          console.log(`BATCH ${chunkNumber} RESULTS:`, chunkResults);

          const cleanCheckLimit = pLimit(2);
          const classifyLimit = pLimit(8);
    
          const classifiedResults = await Promise.all(chunkResults.map((item, index) =>
            classifyLimit(async () => {
              const trackItem = chunk[index];
              const track = trackItem.track;
              let failedFilter = false;
              track.reason = [];
  
              if (item && item.status === 'success') {
                track.trackAnalysis = item;
                if ((chosenFilters.find(filter => filter.id === "profanity")) && item.profanity?.hasProfanity) {
                  track.reason.push("Profanity");
                  failedFilter = true;
                }
                if ((chosenFilters.find(filter => filter.id === "violence")) && item.violence > 0.428) {
                  track.reason.push("Violence");
                  failedFilter = true;
                }
                if ((chosenFilters.find(filter => filter.id === "sexual")) && item.sexually_explicit > 0.50) {
                  track.reason.push("Sexual");
                  failedFilter = true;
                }
                if ((chosenFilters.find(filter => filter.id === "self-harm")) && item.self_harm > 0.50) {
                  track.reason.push("Self-Harm");
                  failedFilter = true;
                }

              } else {
                if (item && item.status === 'failed') {
                  track.reason.push("Error");
                  return { type: 'explicit', track };
                } else {
                  if(item && item.status === 'instrumental'){
                    track.trackAnalysis = item;
                    track.reason.push("Instrumental");
                    failedFilter = false;
                  }else{
                    track.reason.push("No score");
                    failedFilter = true;
                  }
                }
              }
  
              if (failedFilter) {
                let replaceCleanVersion = (chosenFilters.find(filter => filter.id === "profanity"))?.options.replaceClean
                if ((track.reason.length === 1) && (track.reason[0] === "Profanity") && replaceCleanVersion && (track.trackAnalysis.profanity?.customBlacklistedWordsFound.length === 0)) {
                  if (track.explicit) {
                    return { type: 'needs-clean-search', track };
                  }
                  const cleanCheckStart = Date.now();
                  const cleanCheck = await cleanCheckLimit(() => isCleanVersion(track));
                  console.log(`isCleanVersion took ${Date.now() - cleanCheckStart}ms for track ${track.name}`);
                  if (cleanCheck.isClean) {
                    track.reason = ["clean version"];
                    return { type: 'clean', track };
                  } else {
                    return { type: 'explicit', track };
                  }
                } else {
                  return { type: 'explicit', track };
                }
              } else {
                  if(track.explicit && track.trackAnalysis?.profanity && track.trackAnalysis.profanity.whitelistedWordsFound?.length === 0){
                    track.reason.push("check manually");
                  }                 
                track.reason.push("passed filters");
                return { type: 'clean', track };
              }
            })
          ));

          console.log("CLASSIFIED RESULTS: ", classifiedResults)
  
          for (const result of classifiedResults) {
            if (result.type === 'clean') {
              results.cleanTracks.push(result.track);
            } else if (result.type === 'explicit') {
              results.explicitTracks.push(result.track);
            } else if (result.type === 'needs-clean-search') {
              results.tracksNeedingCleanSearch.push(result.track);
            }
          }

        // Update progress after each batch completes
        processedSongs += chunk.length;
        const percentage = 5 + Math.floor((processedSongs / tracks.length) * 85); // 5% to 90%
        onProgressUpdate(percentage, 'analyzing', processedSongs);

        } catch (error) {

          if (error.name === 'AbortError') {
            throw error; // Propagate abort
          }
          console.error(`CHUNK ${chunkNumber} FAILED:`, error);
          chunk.forEach(trackItem => {
            const track = trackItem.track;
            track.reason = ["failed"];
            results.explicitTracks.push(track);
          });

          processedSongs += chunk.length;
          const percentage = 5 + Math.floor((processedSongs / tracks.length) * 85);
          onProgressUpdate(percentage, 'starting-analysis');
        }
  
        console.log(`CHUNK ${chunkNumber} took ${Date.now() - chunkStart}ms`);
      }));
    }
  
    await Promise.all(chunkPromises);

    
    return results;
  };
  
  

  const isCleanVersion = async (track) => {
    try {
      let localItemName = track.name.toLowerCase();
      if (track.name.includes("-") && !track.name.toLowerCase().includes("- remix")) {
        localItemName = track.name.substring(0, track.name.indexOf("-")).trim().toLowerCase();
      }
      
      const artist = track.artists[0].name.toLowerCase();
      
      const query = `track:"${localItemName.replace(/"/g, '')}" artist:"${artist.replace(/"/g, '')}"`;
      
      const searchResult = await rateLimitedApi.call(
        spotifyApi.search.bind(spotifyApi), 
        query, 
        ['track'], 
        { limit: 5 }
      );
      
      const explicitTrack = searchResult.tracks.items.find(item => {
        const itemName = item.name.toLowerCase();
        const itemArtists = item.artists.map(a => a.name.toLowerCase());
        return item.explicit && itemArtists.includes(artist) && itemName === localItemName;
      });

      if (explicitTrack) {
        return { isClean: true, reason: "clean version exists", track };
      }

      return { isClean: false, reason: "no explicit version found", track };
    } catch (error) {
      console.error('Error finding clean track:', error);
      return { isClean: false, error };
    }
  };    
  
  const findCleanVersions = async (tracks) => {    
    console.log("tracks that need clean versions. in findclean: ", tracks);
    
    const foundCleanTracks = [];

    const batchSize = 10; 
    const batches = [];
    
    for (let i = 0; i < tracks.length; i += batchSize) {
      batches.push(tracks.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const limit = pLimit(3); 
      
      const tasks = batch.map(track => limit(async () => {
        const name = track.name.toLowerCase();
        const artist = track.artists[0].name.toLowerCase();
        const cacheKey = `${name}-${artist}`;
        
        try {          
          const query = `track:"${name.replace(/"/g, '')}" artist:"${artist.replace(/"/g, '')}"`;
          
          const searchResult = await rateLimitedApi.call(
            spotifyApi.search.bind(spotifyApi), 
            query, 
            ['track'], 
            { limit: 5 }
          );
          
          const cleanTrack = searchResult.tracks.items.find(item => {
            const itemName = item.name.toLowerCase();
            const itemArtists = item.artists.map(a => a.name.toLowerCase());
            const isClean = !item.explicit;
            const isSameArtist = itemArtists.includes(artist);
            const isTitleClean = itemName.includes("clean") || itemName.includes("radio version") || itemName.includes("radio edit") || itemName.includes("radio mix");
            const isExactNameMatch = itemName === name;
        
            return isClean && isSameArtist && (isExactNameMatch || isTitleClean);
          });
          
          if (cleanTrack) {
            cleanTrack.reason = [];
            cleanTrack.reason.push("clean version");
            cleanTrack.playlist_track_number = track.playlist_track_number;
            setCachedTrack(cacheKey, cleanTrack);
            return { found: true, track: cleanTrack };
          }
          
          return { found: false };
        } catch (error) {
          console.error('Error finding clean track:', error);
          return { found: false, error };
        }
      }));
      
      const batchResults = await Promise.all(tasks);
      batchResults.forEach(result => {
        if (result.found) {
          foundCleanTracks.push(result.track);
        }
      });
    }

    return foundCleanTracks;
  };

  // Pre-check cache for known clean alternatives
  const preCheckCache = (tracks) => {
    const preFound = [];
    const stillNeedSearch = [];
    console.log("original tracks passed into cache: ", tracks);
    
    tracks.forEach(track => {
      const cacheKey = `${track.name.toLowerCase()}-${track.artists[0].name.toLowerCase()}`;
      
      if (cleanTrackCache[cacheKey] && cleanTrackCache[cacheKey].data) {
        console.log("Clean cache key: ", cleanTrackCache[cacheKey].data);
        const cachedTrack = {...cleanTrackCache[cacheKey].data};
        cachedTrack.playlist_track_number = track.playlist_track_number;
        preFound.push(cachedTrack);
      } else {
        stillNeedSearch.push(track);
      }
    });
    
    return { preFound, stillNeedSearch };
  };
  
  // Main function to prepare the clean playlist
  const prepareCleanPlaylist = async (id) => {
    try {
      const totalStart = Date.now();
      let lastCheckpoint = totalStart;
  
      const logStep = (stepName) => {
        const now = Date.now();
        console.log(`${stepName} took ${now - lastCheckpoint}ms`);
        lastCheckpoint = now;
      };
      
      onProgressUpdate(0, 'start');
  
      console.log("Fetching playlist tracks");
      const playlistTracks = await getPlaylistTracks(id);
      logStep("getPlaylistTracks");
      onProgressUpdate(5, 'start');
  
      console.log("Analyzing tracks data");
      const tracksData = await analyzeTracksData(playlistTracks);
      logStep("analyzeTracksData");
      // analyzeTracksData handles progress from 5% to 90%
  
      console.log("tracks needing clean search: ", tracksData.tracksNeedingCleanSearch);
  
      const { preFound, stillNeedSearch } = preCheckCache(tracksData.tracksNeedingCleanSearch);
      logStep("preCheckCache");
  
      console.log("Finding clean versions for", stillNeedSearch.length, "tracks");
      let newFoundCleanTracks = [];
  
      if(stillNeedSearch.length > 0) {
        onProgressUpdate(92, 'finding-clean-versions');
        newFoundCleanTracks = await findCleanVersions(stillNeedSearch);
      } else {
        newFoundCleanTracks = [];
      }
      logStep("findCleanVersions");
  
      console.log("Finalizing playlist");
      onProgressUpdate(96, 'finalizing');
  
      // Sorting & combining
      const trackUris = new Set();
      const allCleanTracks = [];
  
      tracksData.cleanTracks.forEach(track => {
        if (!trackUris.has(track.uri)) {
          trackUris.add(track.uri);
          allCleanTracks.push(track);
        }
      });
  
      const allFoundCleanTracks = [...preFound, ...newFoundCleanTracks];
      allFoundCleanTracks.forEach(track => {
        if (!trackUris.has(track.uri)) {
          trackUris.add(track.uri);
          allCleanTracks.push(track);
        }
      });
  
      const foundCleanTrackIds = new Set(allFoundCleanTracks.map(track => track.id));
      const exclTracksNeededClean = stillNeedSearch
        .filter(item => !newFoundCleanTracks.some(clean => 
          clean.name.toLowerCase() === item.name.toLowerCase() && 
          clean.artists[0].name.toLowerCase() === item.artists[0].name.toLowerCase()
        ));
  
      const excludedTracks = [
        ...tracksData.explicitTracks,
        ...exclTracksNeededClean
      ];
  
      allCleanTracks.sort((a,b) => a.playlist_track_number - b.playlist_track_number);
      excludedTracks.sort((a,b) => a.playlist_track_number - b.playlist_track_number);
  
      logStep("sorting & combining");
  
      const totalEnd = Date.now();
      console.log(`Total cleaning time: ${totalEnd - totalStart}ms`);
  
      onProgressUpdate(100, 'complete');
  
      ReactGA.event({
        category: "Playlist",
        action: "Playlist Clean Duration",
        label: playlistId,
        value: totalEnd - totalStart,
        nonInteraction: true,
      });
  
      ReactGA.event({
        category: "Playlist",
        action: "Length of Playlist Cleaned",
        label: playlistId,
        value: totalTrackCount,
        nonInteraction: true,
      });
  
      return {
        name: `${playlistName} (auXmod Version)`,
        tracksAdded: allCleanTracks,
        excludedTracks: excludedTracks,
      };
    } catch (error) {
      console.error("Error creating clean playlist:", error);
      throw error;
    }
  };
  
  return await prepareCleanPlaylist(playlistId, onProgressUpdate);
};

export default CleanPlaylist;